import { z } from 'zod'
import {
  AccountIdSchema,
  CompanyIdSchema,
  TeamIdSchema,
  UserIdSchema,
  WorkflowIdSchema,
  WorkflowStageIdSchema,
  WorkflowActionIdSchema,
} from '../ids'
import { LocationPlaceIdSchema } from '../googleMaps'
import { AccessStatusSchema } from '../company'
import { CalendarEventIdSchema } from '../calendar'
import { ArchivableSchema } from '../archive'

export const UNIVERSAL_SEARCH_SCOPES = ['Account', 'Calendar', 'Company', 'Team', 'User', 'Location'] as const
export const UniversalSearchScopeSchema = z.enum(UNIVERSAL_SEARCH_SCOPES)
export type UniversalSearchScope = z.infer<typeof UniversalSearchScopeSchema>

const SearchItemSchema = z.object({
  description: z.string(),
  subtitle: z.string().optional(),
})
export type SearchItem = z.infer<typeof SearchItemSchema>

export const AccountSearchItemSchema = z.object({
  ...SearchItemSchema.shape,
  type: z.literal('Account'),
  accountId: AccountIdSchema,
  ownerId: UserIdSchema.optional(),
  workflowId: WorkflowIdSchema.optional(),
  workflowStageId: WorkflowStageIdSchema.optional(),
  workflowActionId: WorkflowActionIdSchema.optional(),
  teamId: TeamIdSchema.optional(),
})
export type AccountSearchItem = z.infer<typeof AccountSearchItemSchema>

export const CalendarSearchItemSchema = z.object({
  ...SearchItemSchema.shape,
  type: z.literal('Calendar'),
  eventId: CalendarEventIdSchema,
})
export type CalendarSearchItem = z.infer<typeof CalendarSearchItemSchema>

export const CompanySearchItemSchema = z.object({
  ...SearchItemSchema.shape,
  type: z.literal('Company'),
  companyId: CompanyIdSchema,
  avatarUrl: z.string(),
  avatarBlurhash: z.string().optional(),
  state: AccessStatusSchema,
})
export type CompanySearchItem = z.infer<typeof CompanySearchItemSchema>

export const TeamSearchItemSchema = z.object({
  ...SearchItemSchema.shape,
  ...ArchivableSchema.shape,
  type: z.literal('Team'),
  teamId: TeamIdSchema,
  avatarUrl: z.string(),
  avatarBlurhash: z.string().optional(),
})
export type TeamSearchItem = z.infer<typeof TeamSearchItemSchema>

export const UserSearchItemSchema = z.object({
  ...SearchItemSchema.shape,
  ...ArchivableSchema.shape,
  type: z.literal('User'),
  userId: UserIdSchema,
  color: z.string().optional(),
  companyId: CompanyIdSchema.meta({ description: "The user's company. `subtitle` holds the company name." }),
  avatarUrl: z.string(),
  avatarBlurhash: z.string().optional(),
})
export type UserSearchItem = z.infer<typeof UserSearchItemSchema>

export const LocationSearchItemSchema = z.object({
  ...SearchItemSchema.shape,
  type: z.literal('Location'),
  placeId: LocationPlaceIdSchema.meta({
    description:
      'The Location result identifier. A value beginning with `TLoc.`, `FA.`, `LocNew.`, `Can.`, or `PR.` is already a supported Terros location ID. Otherwise it is an external address autocomplete identifier that must be exchanged for structured address details and a matched Terros location ID. Legacy `Loc:` values pass schema validation, but the current location reader does not resolve them and returns NotFound.',
  }),
})
export type LocationSearchItem = z.infer<typeof LocationSearchItemSchema>

export const UniversalSearchItemSchema = z.discriminatedUnion('type', [
  AccountSearchItemSchema,
  CalendarSearchItemSchema,
  CompanySearchItemSchema,
  TeamSearchItemSchema,
  UserSearchItemSchema,
  LocationSearchItemSchema,
])
export type UniversalSearchItem = z.infer<typeof UniversalSearchItemSchema>
